import { isMobile, isTablet } from "react-device-detect";
import BreathingExperienceToolbarIcon from "@icons/BreathingIcon.svg";
import VolumeIcon from "@icons/VolumeIcon.svg";
import AppsWidgetToolbarIcon from "@icons/WidgetToolbar/Apps-ToolbarWidget-Icon.svg";
import CalendarWidgetToolbarIcon from "@icons/WidgetToolbar/Calendar-ToolbarWidget-Icon.svg";
import FocusMonitoringWidgetToolbarIcon from "@icons/WidgetToolbar/FocusMonitoringWidgetToolbarIcon.svg";
import FortuneWidgetToolbarIcon from "@icons/WidgetToolbar/Fortune-ToolbarWidget-Icon.svg";
import MediaWidgetToolbarIcon from "@icons/WidgetToolbar/Media-ToolbarWidget-Icon.svg";
import NotesWidgetToolbarIcon from "@icons/WidgetToolbar/Notes-ToolbarWidget-Icon.svg";
import SpacesWidgetToolbarIcon from "@icons/WidgetToolbar/Spaces-ToolbarWidget-Icon.svg";
import TimerWidgetToolbarIcon from "@icons/WidgetToolbar/Timer-ToolbarWidget-Icon.svg";
import ToDoWidgetToolbarIcon from "@icons/WidgetToolbar/ToDo-ToolbarWidget-Icon.svg";
import { FocusMonitoringWidget } from "../focus/FocusMonitoringWidget";
import { WidgetCategory } from "./WidgetWorkspaceTypes";
import FortuneWidget from "./components/Widget/FortuneWidget/FortuneWidget";
import MediaWidget from "./components/Widget/MediaWidget/MediaWidget";
import NotesWidget from "./components/Widget/NotesWidget/NotesWidget";
import { FocusModeTaskListWidget } from "./components/Widget/TaskListWidget/FocusModeTaskListWidget";
import TimerWidget from "./components/Widget/TimerWidget/TimerWidget";

export enum WidgetName {
    SPACES = "Spaces",
    TIMER = "Timer",
    MEDIA = "Media",
    SOUNDBOARD = "Sounds",
    TASK_LIST = "Tasks",
    CALENDAR = "Cal",
    NOTES = "Notes",
    FORTUNE = "Fortune",
    BREATHING = "Breathe",
}

export type WidgetDataType = typeof widgetData;

export interface WidgetEntryType {
    readonly name: WidgetName;
    readonly icon: string;
    readonly showTitle: boolean; // should the widget name be shown in the widget header
    readonly isStatic: boolean; // if the widget is permanent on the canvas
    readonly isPositionFixed?: boolean; // if the widget cannot be moved by dragging
    readonly component: any; // must be any type to avoid compilation issues; TODO: fix this
    readonly requireUserAccount: boolean; // if using the widget requires a user account to be created (e.g. for saving data)
    readonly isResizable: boolean;
    readonly isAccessibleOnMobile?: boolean; // if false, we hide the widget on mobile
    readonly category: WidgetCategory;
}

export const widgetData = {
    SpacesWidget: {
        // TODO: eventually migrate sidebar to use widget framework
        name: WidgetName.SPACES,
        icon: SpacesWidgetToolbarIcon,
        showTitle: false,
        isStatic: false,
        component: null, // Note: component is handled outside the widget framework
        requireUserAccount: false,
        isResizable: false,
        isAccessibleOnMobile: true,
        category: "experiences",
    },
    TimerWidget: {
        name: WidgetName.TIMER,
        icon: TimerWidgetToolbarIcon,
        showTitle: false,
        isStatic: false,
        isPositionFixed: isMobile && !isTablet,
        component: TimerWidget,
        requireUserAccount: false, // widget can still open, but interactions are blocked in Widget.tsx
        isResizable: false,
        isAccessibleOnMobile: true,
        category: "tools",
    },
    TaskListWidget: {
        name: WidgetName.TASK_LIST,
        icon: ToDoWidgetToolbarIcon,
        showTitle: false, // shows a custom header
        isStatic: false,
        isPositionFixed: isMobile && !isTablet,
        component: FocusModeTaskListWidget,
        requireUserAccount: true,
        isResizable: !isMobile || isTablet,
        isAccessibleOnMobile: true,
        category: "tools",
    },
    CalendarWidget: {
        // TODO: eventually migrate sidebar to use widget framework
        name: WidgetName.CALENDAR,
        icon: CalendarWidgetToolbarIcon,
        showTitle: false,
        isStatic: false,
        component: null, // Note: component is handled outside the widget framework
        requireUserAccount: true,
        isResizable: false,
        isAccessibleOnMobile: false,
        category: "tools",
    },
    NotesWidget: {
        name: WidgetName.NOTES,
        icon: NotesWidgetToolbarIcon,
        showTitle: false, // shows a custom header
        isStatic: false,
        component: NotesWidget,
        requireUserAccount: true,
        isResizable: true,
        isAccessibleOnMobile: false,
        category: "tools",
    },
    MediaWidget: {
        name: WidgetName.MEDIA,
        icon: MediaWidgetToolbarIcon,
        showTitle: false,
        isStatic: false,
        component: MediaWidget,
        requireUserAccount: false, // widget can still open, but interactions are blocked in Widget.tsx
        isResizable: true,
        isAccessibleOnMobile: false,
        category: "tools",
    },
    SoundboardWidget: {
        // TODO: eventually migrate sidebar to use widget framework
        name: WidgetName.SOUNDBOARD,
        icon: VolumeIcon,
        showTitle: false,
        isStatic: false,
        component: null, // Note: component is handled outside the widget framework
        requireUserAccount: true,
        isResizable: false,
        isAccessibleOnMobile: false,
        category: "tools",
    },
    FortuneWidget: {
        name: WidgetName.FORTUNE,
        icon: FortuneWidgetToolbarIcon,
        showTitle: false, // shows a custom header
        isStatic: false,
        component: FortuneWidget,
        requireUserAccount: true,
        isResizable: false,
        isAccessibleOnMobile: false,
        category: "tools",
    },
    BreathingWidget: {
        name: WidgetName.BREATHING,
        icon: BreathingExperienceToolbarIcon,
        showTitle: false,
        isStatic: false,
        // Component is handled in WidgetToolbar.tsx
        component: null,
        requireUserAccount: false,
        isResizable: false,
        isAccessibleOnMobile: false,
        category: "experiences",
    },
} as const satisfies Record<string, WidgetEntryType>;

export interface WidgetDataManagerType {
    getWidgetKeys: (filter?: (widgetEntry: WidgetEntryType) => boolean) => string[];
}
export default function WidgetDataManager(): WidgetDataManagerType {
    const getWidgetKeys = (filter?: (widgetEntry: WidgetEntryType) => boolean): string[] => {
        let widgetKeys: string[] = [];
        for (const key in widgetData) {
            const widget = widgetData[key];
            if (widget && (!filter || filter(widget))) {
                widgetKeys.push(key);
            }
        }
        return widgetKeys;
    };

    return {
        getWidgetKeys: getWidgetKeys,
    };
}
